import { defineComponent as _defineComponent } from 'vue'
import { onMounted, ref } from 'vue';
import { isIpAddress, required, isValidLength } from '@/lib/rule';
import { defineMessages } from '@/lib/i18n';
import sharedMessages from '@/lib/shared-messages';
import { store } from '@/store/index';
import {
  dispatchCreateCompanyWhitelistIp,
  dispatchCreateCompanyWhitelistIpBulk,
  dispatchGetClientIp,
} from '@/store/main/actionsMain';
import { AxiosError } from 'axios';
import { commitAddNotification } from '@/store/main/mutations';
import { readAssertedCompanyId } from '@/store/main/getters';
import { CompanyWhiteListIpGetResponse } from '@/interfaces/api/general/company';

type FormData = {
  ipAddress: string;
  name: string;
  clientIpList: string[];
};


export default /*#__PURE__*/_defineComponent({
  __name: 'AddIpAddress',
  props: {
    ipAddressList: null
  },
  emits: ["refresh:ipAddressList"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'refresh:ipAddressList'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  ipAddressList: CompanyWhiteListIpGetResponse['data'];
};





const showWhitelistBulkRegisterCsv = ref<boolean>(false);
const showAddWhitelistIpAddress = ref<boolean>(false);
const formData = ref<Partial<FormData>>({});
const isValidForm = ref<boolean>(false);
const clientIpList = ref<string[]>([]);
const uploadProgress = ref<number>(0);
const isValidFile = ref<boolean>(true);

const m = defineMessages({
  ipAddressInputHelp:
    '単一IPアドレス または CIDR形式でのレンジ指定が可能です。',
  cidrFormatLabel: 'CIDR形式について',
  cidrFormatExplanation:
    'CIDR（シーダー）形式は、IPアドレスの範囲を「192.168.0.0/24」のように指定する方法です。たとえばこの例は、「192.168.0.1~192.168.0.254」のような複数のIPをまとめて指定しています。',
});

const updateIsValidForm = (isValid: boolean) => {
  isValidForm.value = isValid;
};

const updateIpAddress = (value: string) => {
  formData.value = {
    ...formData.value,
    ipAddress: value,
  };
};
const updateName = (value: string) => {
  formData.value = {
    ...formData.value,
    name: value,
  };
};

const openAddIpModal = () => {
  showAddWhitelistIpAddress.value = true;
  showWhitelistBulkRegisterCsv.value = false;
};

const openBulkCsvModal = () => {
  showWhitelistBulkRegisterCsv.value = true;
  showAddWhitelistIpAddress.value = false;
};

const closeAllModals = () => {
  showWhitelistBulkRegisterCsv.value = false;
  showAddWhitelistIpAddress.value = false;
};

const removeCSVFile = () => {
  csvFile.value = null;
  validEntries.value = [];
  if (csvInput.value) {
    csvInput.value.value = '';
  }
  uploadProgress.value = 0;
};

const postAddWhitelistIpAddress = async (): Promise<void> => {
  // the data already validated by v-form
  const data = formData.value as FormData;

  try {
    await dispatchCreateCompanyWhitelistIp(store, {
      payload: {
        ip_address: data.ipAddress,
        name: data.name,
      },
      companyId: readAssertedCompanyId(store),
    });
    commitAddNotification(store, {
      messageKey: sharedMessages.ipAddressAdded,
      color: 'success',
    });
  } catch (error) {
    console.error(error);
    if (error instanceof AxiosError) {
      commitAddNotification(store, {
        messageKey: sharedMessages.responseError,
        messageValues: {
          detail: error.response?.data ?? error.message,
        },
        color: 'error',
      });
    } else {
      commitAddNotification(store, {
        messageKey: sharedMessages.genericError,
        color: 'error',
      });
    }
  } finally {
    showAddWhitelistIpAddress.value = false;
    formData.value = {};
    emits('refresh:ipAddressList');
  }
};

const downloadSampleCsv = () => {
  const sampleCsvContent = `ホワイトIPアドレス / White IP address,名前 (管理用) / Name (for management)
192.168.0.1,社内サーバー(*サンプルのため削除してください)
192.168.0.2,プリンタ1(*サンプルのため削除してください)
192.168.0.3,VPNゲートウェイ(*サンプルのため削除してください)
10.0.0.1,開発用サーバー(*サンプルのため削除してください)
172.16.0.1,バックアップサーバー(*サンプルのため削除してください)
172.11.2.0/24,本社全体で使用されているIP(*サンプルのため削除してください)`;

  const bom = '\uFEFF';
  const blob = new Blob([bom + sampleCsvContent], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'sample_ip_list.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const csvInput = ref<HTMLInputElement | null>(null);
const openCsvUpload = () => {
  csvInput.value?.click();
};

const csvFile = ref<File | null>(null);
const validEntries = ref<{ ip: string; name: string }[]>([]);

const onDragOver = (event: DragEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

const handleCsvDrop = async (event: DragEvent): Promise<void> => {
  event.preventDefault();
  event.stopPropagation();

  const files = Array.from(event.dataTransfer?.files || []).filter((f) =>
    f.name.toLowerCase().endsWith('.csv')
  );

  if (!files.length) return;

  await processCsvFile(files);

  if (csvInput.value) {
    csvInput.value.value = '';
  }
};

const storeCsvFile = async (event: Event) => {
  const files = Array.from((event.target as HTMLInputElement).files || []);
  const input = event.target as HTMLInputElement;
  const file = files[0];
  csvFile.value = file;
  input.value = '';

  if (!file.name.toLowerCase().endsWith('.csv')) {
    isValidFile.value = false;
    commitAddNotification(store, {
      messageKey: sharedMessages.onlyReceiveCsvFile,
      color: 'error',
    });
    csvFile.value = null;
    validEntries.value = [];
    uploadProgress.value = 0;
    return;
  }

  if (file.size > 2 * 1024 * 1024) {
    isValidFile.value = false;
    commitAddNotification(store, {
      messageKey: sharedMessages.fileSizeExceeded,
      color: 'error',
    });
    csvFile.value = null;
    validEntries.value = [];
    uploadProgress.value = 0;
    return;
  }

  isValidFile.value = true;
  await processCsvFile(files);
};

const isValidEntryCheck = ref<boolean>(false);

const processCsvFile = async (files: File[]) => {
  if (!files.length) return;

  isValidEntryCheck.value = false;

  const file = files[0];
  csvFile.value = file;
  validEntries.value = [];
  const ipDuplicationErrors: number[] = [];
  const checkDuplicationInCsvFile = new Set<string>();
  const ipFormatErrors: number[] = [];
  const text = await file.text();
  const rows = text.trim().split('\n');
  uploadProgress.value = 0;
  const dataRows = rows.slice(1);
  const nameErrors: number[] = [];

  for (const [index, row] of dataRows.entries()) {
    const [ipRaw, nameRaw = ''] = row.split(',');
    const rowNumber = index + 1;
    const ip = ipRaw?.trim() || '';
    const name = nameRaw?.trim() || '';
    const ipValidationResult = isIpAddress(ip);
    if (ipValidationResult !== true) {
      ipFormatErrors.push(rowNumber);
      continue;
    }

    const nameValidationResult = isValidLength(45)(name);
    if (!name || nameValidationResult !== true) {
      nameErrors.push(rowNumber);
      continue;
    }

    if (props.ipAddressList.some((ipObj) => ipObj.ip_address === ip)) {
      ipDuplicationErrors.push(rowNumber);
      continue;
    }
    if (checkDuplicationInCsvFile.has(ip)) {
      ipDuplicationErrors.push(rowNumber);
      continue;
    }

    checkDuplicationInCsvFile.add(ip);
    validEntries.value.push({ ip, name });
  }
  uploadProgress.value = 100;

  if (ipFormatErrors.length > 0) {
    const adjustIpFormatErrors = ipFormatErrors
      .sort((a, b) => a - b)
      .join(', ');
    commitAddNotification(store, {
      messageKey: sharedMessages.invalidIpFormat,
      messageValues: { row: adjustIpFormatErrors },
      color: 'error',
    });
  }

  if (ipDuplicationErrors.length > 0) {
    const adjustIpDuplicationErrors = ipDuplicationErrors
      .sort((a, b) => a - b)
      .join(', ');
    commitAddNotification(store, {
      messageKey: sharedMessages.duplicateIpAddress,
      messageValues: { row: adjustIpDuplicationErrors },
      color: 'error',
    });
  }

  if (nameErrors.length > 0) {
    const adjustNameErrors = nameErrors.sort((a, b) => a - b).join(', ');
    commitAddNotification(store, {
      messageKey: sharedMessages.invalidNameFormat,
      messageValues: {
        row: adjustNameErrors,
      },
      color: 'error',
    });
  }

  isValidEntryCheck.value =
    ipFormatErrors.length > 0 ||
    ipDuplicationErrors.length > 0 ||
    nameErrors.length > 0;
};

const submitCsvFile = async () => {
  try {
    await dispatchCreateCompanyWhitelistIpBulk(store, {
      payload: {
        ip_address_list: validEntries.value.map((entry) => ({
          ip_address: entry.ip,
          name: entry.name,
        })),
      },
      companyId: readAssertedCompanyId(store),
    });

    commitAddNotification(store, {
      messageKey: sharedMessages.successfullyCsvBulkUpload,
      messageValues: { count: validEntries.value.length },
      color: 'success',
    });
  } catch (error) {
    console.error(error);
    if (error instanceof AxiosError) {
      commitAddNotification(store, {
        messageKey: sharedMessages.responseError,
        messageValues: {
          detail: error.response?.data ?? error.message,
        },
        color: 'error',
      });
    } else {
      commitAddNotification(store, {
        messageKey: sharedMessages.genericError,
        color: 'error',
      });
    }
  } finally {
    showWhitelistBulkRegisterCsv.value = false;
    emits('refresh:ipAddressList');
    validEntries.value = [];
    csvFile.value = null;
    uploadProgress.value = 0;
  }
};

const getClientIp = async (): Promise<void> => {
  const response = await dispatchGetClientIp(store);

  if (!response) {
    return;
  }

  // APIは現在、ip_addressesという配列を返します
  clientIpList.value = response.data['ip_addresses'] || [];
};

onMounted(async () => {
  await getClientIp();
});

return { __sfc: true,emits, props, showWhitelistBulkRegisterCsv, showAddWhitelistIpAddress, formData, isValidForm, clientIpList, uploadProgress, isValidFile, m, updateIsValidForm, updateIpAddress, updateName, openAddIpModal, openBulkCsvModal, closeAllModals, removeCSVFile, postAddWhitelistIpAddress, downloadSampleCsv, csvInput, openCsvUpload, csvFile, validEntries, onDragOver, handleCsvDrop, storeCsvFile, isValidEntryCheck, processCsvFile, submitCsvFile, getClientIp, isIpAddress, required, isValidLength, sharedMessages }
}

})